import type { GameEvent, HostToGameMessage } from "./types.ts";

export interface MountOptions {
  /** Full HTML document to load via `srcdoc`. Use this or `src`. */
  html?: string;
  /** URL to load into the iframe. Use this or `html`. */
  src?: string;
  /** Called for every normalised message from the game. */
  onEvent?: (event: GameEvent) => void;
  /** Extra iframe attributes (class, title, allow, ...). */
  attributes?: Record<string, string>;
}

export interface GameHandle {
  iframe: HTMLIFrameElement;
  /** Send a raw host message to the game. */
  post(message: HostToGameMessage): void;
  /** Override the game's focus signal with an external value in [0,1]. */
  setFocus(value: number): void;
  /** Return focus control to the game's own simulation. */
  clearFocus(): void;
  /** Report the focus signal's quality; while "bad", games ignore the focus value. */
  setSignal(quality: "good" | "bad"): void;
  /** Hand the game saved progress (a `progress` detail it sent earlier) to resume from. Send it after `ready`. */
  restore(detail: Record<string, unknown>): void;
  /** Remove the iframe and listeners. */
  destroy(): void;
}

/**
 * Normalises messages coming out of a game iframe. Accepts the standard
 * `myndland-game` envelope and the legacy `{type:"waterwaySolved"}` message.
 */
export function normalizeGameMessage(data: unknown): GameEvent | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (d.source === "myndland-game" && typeof d.type === "string") {
    if (d.type === "marker") {
      // Markers become research records: insist on an action, keep the text fields strings, the score an integer.
      if (typeof d.action !== "string" || !d.action) return null;
      const ev: Extract<GameEvent, { type: "marker" }> = {
        source: "myndland-game",
        game: String(d.game),
        level: String(d.level),
        t: typeof d.t === "number" && Number.isFinite(d.t) ? d.t : Date.now(),
        type: "marker",
        action: d.action,
        question: typeof d.question === "string" ? d.question : "",
        answer: typeof d.answer === "string" ? d.answer : "",
      };
      if (typeof d.score === "number" && Number.isSafeInteger(d.score)) ev.score = d.score;
      if (d.choice === "correct" || d.choice === "related" || d.choice === "irrelevant") ev.choice = d.choice;
      return ev;
    }
    return d as unknown as GameEvent;
  }
  if (d.type === "waterwaySolved") {
    return {
      source: "myndland-game",
      game: "waterway",
      level: typeof d.engine === "string" ? d.engine.toLowerCase() : "unknown",
      t: Date.now(),
      type: "solved",
      detail: { legacy: true },
    };
  }
  return null;
}

/** Mounts a game level into `container` inside an iframe and wires up the message bridge. */
export function mountGame(container: HTMLElement, opts: MountOptions): GameHandle {
  if (!opts.html && !opts.src) throw new Error("mountGame: provide `html` or `src`");
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Myndland game");
  iframe.setAttribute("allow", "autoplay");
  iframe.style.border = "0";
  iframe.style.width = "100%";
  iframe.style.height = "100%";
  for (const [k, v] of Object.entries(opts.attributes ?? {})) iframe.setAttribute(k, v);

  const onMessage = (e: MessageEvent) => {
    if (e.source !== iframe.contentWindow) return;
    const ev = normalizeGameMessage(e.data);
    if (ev) opts.onEvent?.(ev);
  };
  window.addEventListener("message", onMessage);

  if (opts.src) iframe.src = opts.src;
  else iframe.srcdoc = opts.html!;
  container.appendChild(iframe);

  const post = (message: HostToGameMessage) => {
    iframe.contentWindow?.postMessage({ source: "myndland-host", ...message }, "*");
  };

  return {
    iframe,
    post,
    setFocus: (value) => post({ type: "focus", value }),
    clearFocus: () => post({ type: "focus", value: null }),
    setSignal: (quality) => post({ type: "signal", quality }),
    restore: (detail) => post({ type: "restore", detail }),
    destroy: () => {
      window.removeEventListener("message", onMessage);
      iframe.remove();
    },
  };
}
